import { Button, Input, message, Radio, Select, Spin, Typography } from 'antd';
import { useTranslation } from '../../../i18n/i18n';
import { EditOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import './EditLocationModal.scss';
import { locationApi } from '../../../api/location.api';
import { installationApi } from '../../../api/installation.api';
import { Location } from '../../../gen/proto/api/gateway/v1/location_pb';
import { Installation } from '../../../gen/proto/api/gateway/v1/installation_pb';
import { deviceApi } from '../../../api/device';
import ModalCustom from '../../utils/ModalCustom/ModalCustom';
import { DeviceTrackerType } from '../../../gen/proto/api/gateway/v1/device_tracker_pb';

const { Text } = Typography;
const FIRST_PAGE = 1;
const PAGE_SIZE = 10;

interface EditLocationModalProps {
  deviceName: string;
  deviceUuid: string;
  onSaved: () => void;
}

const EditLocationModal = ({ deviceName, deviceUuid, onSaved }: EditLocationModalProps) => {
  const { t } = useTranslation('devices', { keyPrefix: 'history' });
  const [messageApi, contextHolder] = message.useMessage();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [optionSelected, setOptionSelected] = useState<DeviceTrackerType>(DeviceTrackerType.INSTALLATION);
  const [selectedLocationUuid, setSelectedLocationUuid] = useState<string | null>(null);
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [loadingInstallations, setLoadingInstallations] = useState<boolean>(false);
  const [selectedInstallationUuid, setSelectedInstallationUuid] = useState<string | undefined>(undefined);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setSelectedLocationUuid(null);
    setInstallations([]);
    setSelectedInstallationUuid(undefined);
    if (!value.trim()) {
      setLocations([]);
    }
  };

  const handleSearchSubmit = () => {
    if (!searchValue.trim()) {
      setLocations([]);
      return;
    }
    fetchLocations(searchValue);
  };

  const fetchLocations = async (value: string) => {
    try {
      const [addressResponse, erpIdResponse] = await Promise.all([
        locationApi.getListByAddress(value, FIRST_PAGE, PAGE_SIZE),
        locationApi.getListByERPId(value, FIRST_PAGE, PAGE_SIZE),
      ]);
      const merged = [...addressResponse.locations, ...erpIdResponse.locations].filter(
        (loc, i, arr) => arr.findIndex((l) => l.uuid === loc.uuid) === i,
      );
      setLocations(merged);
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    }
  };

  const handleLocationSelect = async (locationUuid: string) => {
    setSelectedLocationUuid(locationUuid);
    setSelectedInstallationUuid(undefined);
    setLoadingInstallations(true);
    try {
      const result = await installationApi.getInstallationByLocationUuid(locationUuid);
      setInstallations(result);
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    } finally {
      setLoadingInstallations(false);
    }
  };

  const selectorOptions = [
    {
      value: DeviceTrackerType.INSTALLATION,
      label: t('instalation'),
    },
    {
      value: DeviceTrackerType.WAREHOUSE,
      label: t('warehouse'),
    },
    {
      value: DeviceTrackerType.OUT_OF_SERVICE,
      label: t('outOfService'),
    },
  ];

  const handleSaveNewInstallation = async () => {
    try {
      await deviceApi.newDeviceLocation(deviceUuid, selectedInstallationUuid ?? '', optionSelected);
      onSaved();
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    } finally {
      setSearchValue('');
      setSelectedLocationUuid(null);
      setLocations([]);
      setInstallations([]);
      setSaveLoading(false);
      setIsModalOpen(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Button type={'primary'} onClick={() => setIsModalOpen(true)}>
        <EditOutlined />
        {t('editLocation')}
      </Button>
      <ModalCustom
        title={`${t('editLocationOf')} ${deviceName}`}
        description={t('editLocationDesc')}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        footer={
          <Button type={'primary'} onClick={handleSaveNewInstallation} loading={saveLoading}>
            {t('save')}
          </Button>
        }
      >
        <div className={'EditLocationModal__content'}>
          <Select
            className={'EditLocationModal__content__selector'}
            defaultValue={optionSelected}
            onChange={(value: DeviceTrackerType) => {
              setOptionSelected(value);
              setSelectedLocationUuid(null);
            }}
            options={selectorOptions}
            popupMatchSelectWidth={false}
          />
          {optionSelected === DeviceTrackerType.INSTALLATION && (
            <>
              <Input
                placeholder={t('locationPlaceholder')}
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                onPressEnter={handleSearchSubmit}
              />
              {locations.length > 0 && (
                <Radio.Group
                  className={'EditLocationModal__content__locations'}
                  value={selectedLocationUuid}
                  onChange={(e) => handleLocationSelect(e.target.value)}
                >
                  {locations.map((loc) => (
                    <Radio key={loc.uuid} value={loc.uuid}>
                      {loc.erpId} — {loc.address}
                    </Radio>
                  ))}
                </Radio.Group>
              )}
              {loadingInstallations && <Spin />}
              {!loadingInstallations && installations.length > 0 && (
                <>
                  <Text>{t('selectInstallation')}</Text>
                  <Select
                    className={'EditLocationModal__content__installations'}
                    placeholder={t('installationPlaceholder')}
                    value={selectedInstallationUuid}
                    onChange={(value: string) => setSelectedInstallationUuid(value)}
                    options={installations.map((inst) => ({ value: inst.uuid, label: inst.name }))}
                  />
                </>
              )}
              {selectedLocationUuid !== null && !loadingInstallations && installations.length === 0 && (
                <Text>{t('noAvailableInstallations')}</Text>
              )}
            </>
          )}
        </div>
      </ModalCustom>
    </>
  );
};

export default EditLocationModal;
